// src/core/services/schoolClient.js
import axios from "axios";
import queryString from "query-string";
import { history, LocalStorageService } from "../../helpers";
import useConfigEnv from "../../zustand";

const localStorageService = LocalStorageService.getService();

// ==== refresh queue ====
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// ==== chờ env từ Zustand ====
function waitForEnvReady() {
  const state = useConfigEnv.getState();
  if (state?.env?.apiEndPoint) return Promise.resolve(state.env);
  return new Promise((resolve) => {
    const unsub = useConfigEnv.subscribe((newState) => {
      if (newState?.env?.apiEndPoint) {
        unsub();
        resolve(newState.env);
      }
    });
  });
}

// tạo instance, baseURL sẽ gắn động trong request interceptor
const schoolClient = axios.create({
  paramsSerializer: (params) => queryString.stringify(params),
});
axios.defaults.timeout = 240000;

// ===== Request interceptor =====
schoolClient.interceptors.request.use(
  async (config) => {
    const env = await waitForEnvReady();
    // gắn baseURL động từ env
    if (!config.baseURL) config.baseURL = env.apiEndPoint;

    // attach token
    const token = localStorageService.getSchoolAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = "Bearer " + token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ===== Response interceptor =====
schoolClient.interceptors.response.use(
  (response) => handleResponse(response),
  async (error) => {
    const originalRequest = error?.config;

    const handleError = (err) => {
      // network error
      if (!err?.response) return err;
      const { data } = err.response;
      if (data && data.code === 400 && ["invalid_grant"].includes(data?.data?.error)) {
        clearAuthToken();
      }
      return data;
    };

    // bảo vệ null
    if (!error?.response || !originalRequest) {
      return Promise.reject(handleError(error));
    }

    // nếu 401 từ /userinfo -> đăng xuất thẳng
    const failedUrl = originalRequest.url || "";
    if (error.response.status === 401 && failedUrl.includes("userinfo")) {
      clearAuthToken();
      window.location.href = "/";
      return Promise.reject(handleError(error));
    }

    const status = error.response.status;
    const code = error.response?.data?.code;

    const isAuthProblem =
      status === 401 ||
      status === 403 ||
      (status === 404 && ["TOKEN_EXPIRED", "AUTHEN_FAIL", 401].includes(code));

    if (!isAuthProblem) {
      return Promise.reject(handleError(error));
    }

    // có request khác đang refresh -> vào hàng đợi
    if (isRefreshing) {
      try {
        const newToken = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers["Authorization"] = "Bearer " + newToken;
        return schoolClient.request(originalRequest);
      } catch (e) {
        if (e?.response?.status === 401) clearAuthToken();
        return Promise.reject(e);
      }
    }

    // bắt đầu refresh
    isRefreshing = true;
    try {
      const env = await waitForEnvReady();
      const refreshToken = localStorageService.getSchoolRefreshToken();
      const accessToken = localStorageService.getSchoolAccessToken();

      if (!refreshToken) {
        clearAuthToken();
        return Promise.reject(handleError(error));
      }

      // Ưu tiên lấy từ env.json; fallback process.env nếu vẫn muốn
      const CLIENT_ID = env.client_id || process.env.REACT_APP_CLIENT_ID || "";
      const CLIENT_SECRET = env.client_secret || process.env.REACT_APP_CLIENT_SECRET || "";

      // Chuẩn OAuth2: x-www-form-urlencoded
      const body = new URLSearchParams({
        refresh_token: refreshToken,
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
      });

      const tokenRes = await axios.post(
        `${env.apiEndPoint}/authentication/oauth2/token`,
        body.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${accessToken || ""}`,
            ...(env.allowed_origin ? { Origin: env.allowed_origin } : {}),
          },
        }
      );

      const data = tokenRes.data;

      // 1) lưu token mới
      localStorageService.setToken(data);
      localStorageService.setRefreshToken(data);

      // 2) cập nhật header
      axios.defaults.headers.common["Authorization"] = "Bearer " + data.access_token;
      originalRequest.headers["Authorization"] = "Bearer " + data.access_token;

      // 3) đánh thức các request đang chờ
      processQueue(null, data.access_token);

      // 4) retry request cũ
      return schoolClient.request(originalRequest);
    } catch (err) {
      // các case refresh fail -> clear token + điều hướng
      const error_code = err?.response?.data?.error_code;
      const invalidGrant =
        (err?.response?.status === 400 || err?.response?.status === 401) &&
        ["invalid_grant"].includes(err?.response?.data?.error);

      if (
        err?.error_code === "AUTHEN_FAIL" ||
        err?.error_code === "TOKEN_EXPIRED" ||
        err?.error_code === "TOKEN_INCORRECT" ||
        err?.code === "ERR_BAD_REQUEST" ||
        error_code === "AUTHEN_FAIL" ||
        invalidGrant
      ) {
        clearAuthToken();
      }

      processQueue(err, null);
      return Promise.reject(handleError(err));
    } finally {
      isRefreshing = false;
    }
  }
);

// ===== helpers =====
const handleResponse = (res) => (res && res.data ? res.data : res);

const clearAuthToken = () => {
  LocalStorageService.clearToken();
  history.push(`/`);
};

export default schoolClient;
